'use client'

import {isUnauthorizedError} from '@/src/shared/utils/expected-error'

import {RunlabLayoutStore} from '../store'
import {observer, useLocalObservable} from 'mobx-react'
import React, {useEffect, useState} from 'react'

import {useQuery, useQueryClient} from '@tanstack/react-query'

import {fetchPageLayoutFn} from '@/src/shared/modules/page/fetch-layout'
import {getInjection} from '@runlab/di'
import {ApiError} from '@/src/ports/services/api'
import {LoadingPage} from '@/src/shared/ui/layout'
import {PageEvents} from '@/src/core/domain/events/page.events'

interface RunlabLayoutProps extends React.PropsWithChildren {
  data: LayoutData
}

const LayoutContext = React.createContext<Nullable<RunlabLayoutStore>>(null)

const withSession = (layout: LayoutData, session: LayoutData['session']): LayoutData => {
  const header = {...layout.header}
  if (session === null || session.user?.id !== layout.session?.user?.id) delete header.cart
  return {...layout, header, session}
}

export const RunlabLayoutProvider = observer(({children, data}: RunlabLayoutProps) => {
  const queryClient = useQueryClient()
  const [initialData] = useState(() => {
    const cachedLayout = queryClient.getQueryData<LayoutData>(['layout'])
    const session = queryClient.getQueryData<LayoutData['session']>(['session'])
    const layout = cachedLayout ?? data
    if (session === undefined || session === layout.session) return layout
    return withSession(layout, session)
  })
  const layoutStore = useLocalObservable(() => new RunlabLayoutStore(initialData))

  // Register initial data without publishing cache writes during React render.
  const sessionState = useQuery({
    queryKey: ['session'],
    queryFn: async () => null,
    initialData: initialData.session,
    enabled: false,
  })

  const queryState = useQuery({
    queryKey: ['layout'],
    queryFn: () => fetchPageLayoutFn(),
    initialData,
    refetchOnMount: initialData.session?.hasPhpSession || initialData.session?.user?.id ? 'always' : false,
  })

  useEffect(() => {
    document.documentElement.style.setProperty(
      '--header-height-desktop',
      `${layoutStore.ui.header.height}px`,
    )
    document.documentElement.style.setProperty(
      '--header-height-mobile',
      `${layoutStore.ui.header.height}px`,
    )
  }, [layoutStore.ui.header.height])

  useEffect(() => {
    if (queryState.data) {
      const session = sessionState.data
      if (session !== undefined && JSON.stringify(session) !== JSON.stringify(queryState.data.session)) {
        const aligned = withSession(queryState.data, session)
        queryClient.setQueryData(['layout'], aligned)
        layoutStore.setData(aligned)
      } else {
        layoutStore.setData(queryState.data)
      }
    }
  }, [queryState.data, sessionState.data, layoutStore, queryClient])

  useEffect(() => {
    if (queryState.error instanceof ApiError) {
      if (isUnauthorizedError(queryState.error)) {
        getInjection('IEventBus').publish(PageEvents.redirect({url: '/login/'}))
      }
    }
  }, [queryState.error])

  if (queryState.error) {
    if (!(queryState.error instanceof ApiError)) {
      return <LoadingPage />
    }
  }

  return <LayoutContext.Provider value={layoutStore}>{children}</LayoutContext.Provider>
})

export const useLayoutStore = (): RunlabLayoutStore => {
  const layoutStore = React.useContext(LayoutContext)

  if (!layoutStore) {
    throw Error('Wrap layout provider')
  }
  return layoutStore
}
